import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { progressAPI, bookmarksAPI, sessionsAPI } from '../../services/api';
import part1Questions from '../../data/questions/part1.json';
import part2Questions from '../../data/questions/part2.json';
import part3Questions from '../../data/questions/part3.json';
import part4Questions from '../../data/questions/part4.json';
import part5Questions from '../../data/questions/part5.json';
import part6Questions from '../../data/questions/part6.json';
import part7Questions from '../../data/questions/part7.json';
import part8Questions from '../../data/questions/part8.json';
import part9Questions from '../../data/questions/part9.json';
import part10Questions from '../../data/questions/part10.json';
import ekgQuestions from '../../data/questions/ekg.json';
import './QuizSetup.css';

const handpQuestionData = {
  '1': part1Questions,
  '2': part2Questions,
  '3': part3Questions,
  '4': part4Questions,
  '5': part5Questions,
  '6': part6Questions,
  '7': part7Questions,
  '8': part8Questions,
  '9': part9Questions,
  '10': part10Questions
};

const handpPartsInfo = [
  { id: 'p1', number: 1, title: 'Gestational & Natal History', module: 'handp' },
  { id: 'p2', number: 2, title: 'Postnatal History', module: 'handp' },
  { id: 'p3', number: 3, title: 'Chief Complaints & Family History', module: 'handp' },
  { id: 'p4', number: 4, title: 'Growth, Inspection & Syndromes', module: 'handp' },
  { id: 'p5', number: 5, title: 'Palpation', module: 'handp' },
  { id: 'p6', number: 6, title: 'Blood Pressure Measurement', module: 'handp' },
  { id: 'p7', number: 7, title: 'Heart Sounds', module: 'handp' },
  { id: 'p8', number: 8, title: 'Systolic Murmurs', module: 'handp' },
  { id: 'p9', number: 9, title: 'Diastolic, Continuous & Innocent Murmurs', module: 'handp' },
  { id: 'p10', number: 10, title: 'Neonatal Cardiac Examination', module: 'handp' }
];

const ekgPartsInfo = [
  { id: 'ekg', number: 'ekg', title: 'Electrocardiography', module: 'ekg' }
];

const allPartsInfo = [...handpPartsInfo, ...ekgPartsInfo];

function QuizSetup() {
  const navigate = useNavigate();

  // Mode selection
  const [mode, setMode] = useState('tutor');

  // Question count
  const [questionCount, setQuestionCount] = useState(20);

  // Module filters
  const [modules, setModules] = useState({
    handp: true,
    ekg: true
  });

  // Part filters
  const [selectedParts, setSelectedParts] = useState(
    allPartsInfo.reduce((acc, part) => ({ ...acc, [part.id]: true }), {})
  );

  // Difficulty filters
  const [difficulties, setDifficulties] = useState({
    easy: true,
    medium: true,
    hard: true
  });

  // Special filters
  const [unusedOnly, setUnusedOnly] = useState(false);
  const [incorrectOnly, setIncorrectOnly] = useState(false);
  const [bookmarkedOnly, setBookmarkedOnly] = useState(false);

  // Timer settings
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(60);

  // User data for filtering
  const [answeredQuestions, setAnsweredQuestions] = useState({});
  const [incorrectQuestions, setIncorrectQuestions] = useState(new Set());
  const [bookmarkedQuestions, setBookmarkedQuestions] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Load user progress data
  useEffect(() => {
    async function loadData() {
      try {
        const bookmarksData = await bookmarksAPI.getAll();

        // Load answers for all H&P parts
        const allAnswers = {};
        const incorrect = new Set();

        for (const part of handpPartsInfo) {
          try {
            const partData = await progressAPI.getPart(part.number);
            if (partData.answers) {
              Object.entries(partData.answers).forEach(([qId, answer]) => {
                allAnswers[qId] = answer;
                if (!answer.correct) {
                  incorrect.add(qId);
                }
              });
            }
          } catch (e) {
            // Part not started yet
          }
        }

        setAnsweredQuestions(allAnswers);
        setIncorrectQuestions(incorrect);

        if (bookmarksData.bookmarks) {
          setBookmarkedQuestions(new Set(bookmarksData.bookmarks.map(b => b.question_id)));
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
      } finally {
        setIsLoading(false);
      }
    }

    loadData();
  }, []);

  // Update part selection when module changes
  useEffect(() => {
    setSelectedParts(prev => {
      const updated = { ...prev };
      handpPartsInfo.forEach(part => {
        updated[part.id] = modules.handp && prev[part.id];
      });
      ekgPartsInfo.forEach(part => {
        updated[part.id] = modules.ekg && prev[part.id];
      });
      return updated;
    });
  }, [modules]);

  // Get all questions from selected parts
  const allQuestions = useMemo(() => {
    const questions = [];

    // H&P questions
    if (modules.handp) {
      Object.entries(handpQuestionData).forEach(([partNum, data]) => {
        if (selectedParts[`p${partNum}`]) {
          data.questions.forEach(q => {
            questions.push({ ...q, partNumber: parseInt(partNum, 10), module: 'handp' });
          });
        }
      });
    }

    // EKG questions
    if (modules.ekg && selectedParts['ekg']) {
      ekgQuestions.questions.forEach(q => {
        questions.push({ ...q, partNumber: 'ekg', module: 'ekg' });
      });
    }

    return questions;
  }, [modules, selectedParts]);

  // Filter questions based on criteria
  const filteredQuestions = useMemo(() => {
    return allQuestions.filter(q => {
      // Difficulty filter
      const difficulty = q.difficulty || 'medium';
      if (!difficulties[difficulty]) return false;

      // Unused filter
      if (unusedOnly && answeredQuestions[q.id]) return false;

      // Incorrect filter
      if (incorrectOnly && !incorrectQuestions.has(q.id)) return false;

      // Bookmarked filter
      if (bookmarkedOnly && !bookmarkedQuestions.has(q.id)) return false;

      return true;
    });
  }, [allQuestions, difficulties, unusedOnly, incorrectOnly, bookmarkedOnly, answeredQuestions, incorrectQuestions, bookmarkedQuestions]);

  const availableCount = filteredQuestions.length;
  const effectiveQuestionCount = Math.min(questionCount, availableCount);

  const toggleModule = (mod) => {
    setModules(prev => ({ ...prev, [mod]: !prev[mod] }));
  };

  const togglePart = (partId) => {
    setSelectedParts(prev => ({ ...prev, [partId]: !prev[partId] }));
  };

  const selectAllParts = (mod) => {
    const partsToSelect = mod === 'handp' ? handpPartsInfo : ekgPartsInfo;
    setSelectedParts(prev => {
      const updated = { ...prev };
      partsToSelect.forEach(part => {
        updated[part.id] = true;
      });
      return updated;
    });
  };

  const clearAllParts = (mod) => {
    const partsToClear = mod === 'handp' ? handpPartsInfo : ekgPartsInfo;
    setSelectedParts(prev => {
      const updated = { ...prev };
      partsToClear.forEach(part => {
        updated[part.id] = false;
      });
      return updated;
    });
  };

  const toggleDifficulty = (diff) => {
    setDifficulties(prev => ({ ...prev, [diff]: !prev[diff] }));
  };

  const handleStartQuiz = async () => {
    if (effectiveQuestionCount === 0) return;

    // Shuffle and select questions
    const shuffled = [...filteredQuestions].sort(() => Math.random() - 0.5);
    const selected = shuffled.slice(0, effectiveQuestionCount);
    const questionIds = selected.map(q => q.id);

    try {
      const result = await sessionsAPI.create(
        mode,
        questionIds,
        timerEnabled ? timerSeconds : null
      );

      navigate(`/quiz/${result.sessionId}`);
    } catch (err) {
      console.error('Failed to create quiz session:', err);
      alert('Failed to start quiz. Please try again.');
    }
  };

  // Count questions per module
  const handpCount = allQuestions.filter(q => q.module === 'handp').length;
  const ekgCount = allQuestions.filter(q => q.module === 'ekg').length;

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="quiz-setup">
      <div className="quiz-setup-header">
        <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>Create Custom Quiz</h1>
      </div>

      <div className="quiz-setup-card">
        <section className="setup-section">
          <h3>Mode</h3>
          <div className="mode-options">
            <label className={`mode-option ${mode === 'tutor' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="mode"
                value="tutor"
                checked={mode === 'tutor'}
                onChange={() => setMode('tutor')}
              />
              <div className="mode-content">
                <strong>Tutor Mode</strong>
                <span>See feedback after each question</span>
              </div>
            </label>
            <label className={`mode-option ${mode === 'test' ? 'selected' : ''}`}>
              <input
                type="radio"
                name="mode"
                value="test"
                checked={mode === 'test'}
                onChange={() => setMode('test')}
              />
              <div className="mode-content">
                <strong>Test Mode</strong>
                <span>See all results at the end</span>
              </div>
            </label>
          </div>
        </section>

        <section className="setup-section">
          <h3>Modules</h3>
          <div className="module-options">
            <label className={`module-checkbox ${modules.handp ? 'selected' : ''}`}>
              <input
                type="checkbox"
                checked={modules.handp}
                onChange={() => toggleModule('handp')}
              />
              <span>H&P Assessment</span>
              <small>144 questions</small>
            </label>
            <label className={`module-checkbox ${modules.ekg ? 'selected' : ''}`}>
              <input
                type="checkbox"
                checked={modules.ekg}
                onChange={() => toggleModule('ekg')}
              />
              <span>EKG Assessment</span>
              <small>39 questions</small>
            </label>
          </div>
        </section>

        <section className="setup-section">
          <h3>Number of Questions</h3>
          <div className="question-count-slider">
            <input
              type="range"
              min="5"
              max={Math.max(availableCount, 5)}
              value={Math.min(questionCount, availableCount)}
              onChange={(e) => setQuestionCount(parseInt(e.target.value, 10))}
            />
            <span className="count-display">{effectiveQuestionCount}</span>
          </div>
          <div className="count-presets">
            {[5, 10, 20, 50].map(n => (
              <button
                key={n}
                className={`preset-btn ${questionCount === n ? 'active' : ''}`}
                onClick={() => setQuestionCount(n)}
                disabled={availableCount < n}
              >
                {n}
              </button>
            ))}
            <button
              className={`preset-btn ${questionCount >= availableCount ? 'active' : ''}`}
              onClick={() => setQuestionCount(availableCount)}
            >
              All ({availableCount})
            </button>
          </div>
        </section>

        {modules.handp && (
          <section className="setup-section">
            <h3>H&P Topics</h3>
            <div className="part-actions">
              <button className="btn-link" onClick={() => selectAllParts('handp')}>Select All</button>
              <button className="btn-link" onClick={() => clearAllParts('handp')}>Clear All</button>
              <span className="part-count">{handpCount} questions selected</span>
            </div>
            <div className="parts-grid">
              {handpPartsInfo.map(part => (
                <label key={part.id} className="part-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedParts[part.id]}
                    onChange={() => togglePart(part.id)}
                  />
                  <span>Part {part.number}: {part.title}</span>
                </label>
              ))}
            </div>
          </section>
        )}

        {modules.ekg && (
          <section className="setup-section">
            <h3>EKG Topics</h3>
            <div className="part-actions">
              <span className="part-count">{ekgCount} questions selected</span>
            </div>
            <div className="parts-grid">
              {ekgPartsInfo.map(part => (
                <label key={part.id} className="part-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedParts[part.id]}
                    onChange={() => togglePart(part.id)}
                  />
                  <span>{part.title}</span>
                </label>
              ))}
            </div>
          </section>
        )}

        <section className="setup-section">
          <h3>Difficulty</h3>
          <div className="difficulty-options">
            {['easy', 'medium', 'hard'].map(diff => (
              <label key={diff} className={`difficulty-checkbox ${diff}`}>
                <input
                  type="checkbox"
                  checked={difficulties[diff]}
                  onChange={() => toggleDifficulty(diff)}
                />
                <span>{diff.charAt(0).toUpperCase() + diff.slice(1)}</span>
              </label>
            ))}
          </div>
        </section>

        <section className="setup-section">
          <h3>Special Filters</h3>
          <div className="special-filters">
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={unusedOnly}
                onChange={() => setUnusedOnly(!unusedOnly)}
              />
              <span>Unused questions only</span>
              <small>({allQuestions.filter(q => !answeredQuestions[q.id]).length} available)</small>
            </label>
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={incorrectOnly}
                onChange={() => setIncorrectOnly(!incorrectOnly)}
              />
              <span>Previously incorrect only</span>
              <small>({incorrectQuestions.size} available)</small>
            </label>
            <label className="filter-checkbox">
              <input
                type="checkbox"
                checked={bookmarkedOnly}
                onChange={() => setBookmarkedOnly(!bookmarkedOnly)}
              />
              <span>Bookmarked only</span>
              <small>({bookmarkedQuestions.size} available)</small>
            </label>
          </div>
        </section>

        <section className="setup-section">
          <h3>Timer</h3>
          <div className="timer-settings">
            <label className="timer-toggle">
              <input
                type="checkbox"
                checked={timerEnabled}
                onChange={() => setTimerEnabled(!timerEnabled)}
              />
              <span>Enable timer</span>
            </label>
            {timerEnabled && (
              <div className="timer-input">
                <input
                  type="number"
                  min="10"
                  max="300"
                  value={timerSeconds}
                  onChange={(e) => setTimerSeconds(Math.max(10, Math.min(300, parseInt(e.target.value, 10) || 60)))}
                />
                <span>seconds per question</span>
              </div>
            )}
          </div>
        </section>

        <div className="quiz-summary">
          <p>
            <strong>{effectiveQuestionCount}</strong> questions available with current filters
          </p>
          {effectiveQuestionCount === 0 && (
            <p className="no-questions-warning">
              No questions match your filters. Try adjusting your selection.
            </p>
          )}
        </div>

        <button
          className="btn btn-primary start-quiz-btn"
          onClick={handleStartQuiz}
          disabled={effectiveQuestionCount === 0}
        >
          Start Quiz
        </button>
      </div>
    </div>
  );
}

export default QuizSetup;
